import { useMemo } from 'react';
import { isExpressionValueActive } from '../../../schema/expressions/evaluate/getExpressionValue.ts';
import { useExpressionValue } from '../../../schema/expressions/render/hooks/useExpressionValue.tsx';
import { useWidgetScopeContext } from '../../../schema/form/fillable/WidgetScopeContext.tsx';
import { useWidgetComponentContext } from '../../../schema/widgets/context/WidgetComponentContext.tsx';
import { InfoIcon } from '../../../ui/info-icon/InfoIcon.tsx';
import { StyledRichTextAdvancedText } from '../../rich-text/style/StyledRichTextAdvancedText.tsx';
import { type IAdvancedRichTextItem } from '../IAdvancedRichText.ts';
import { renderRichTextAdvancedNodes } from './renderRichTextAdvancedNodes.tsx';

export interface IRichTextConditionalProps {
  item: IAdvancedRichTextItem;
  index: number;
  inline?: boolean;
  hasCustomFont?: boolean;
  isInfoIcon?: boolean;
}

/**
 * Renderizza condizionalmente un nodo di RTE avanzato, in base alle
 * regole definite.
 *
 * Ottimizzazioni:
 * - Skip della hook chain useExpressionValue quando non c'è regola (la maggior parte dei casi)
 * - Memoizzazione del risultato di renderRichTextAdvancedNodes (html-react-parser)
 */
export function RichTextConditional(props: IRichTextConditionalProps) {
  const { item, inline, hasCustomFont } = props;

  const { preview } = useWidgetComponentContext();

  const widgetScope = useWidgetScopeContext();
  // Creiamo un path "virtuale" per il nodo corrente;
  const basePath = `${widgetScope.prefix ?? ''}virtualNode`;

  // Cortocircuita la hook chain completa (useMaterializedExpressionReferences,
  // useWatchFormReferences, useWatch) quando non c'è regola condizionale.
  const hasRule = !!item.rule;
  const visible = useExpressionValue(basePath, item.rule, {
    skip: preview || !hasRule,
  });

  // Memoizza il risultato di html-react-parser: item.text è stabile
  // (proviene dalla configurazione widget) e non cambia tra i render.
  const renderedNodes = useMemo(
    () => renderRichTextAdvancedNodes(item.text, preview),
    [item.text, preview],
  );

  if (hasRule && !isExpressionValueActive(visible) && !preview) {
    return null;
  }

  if (props.isInfoIcon) {
    return <InfoIcon text={renderedNodes} />;
  }

  return (
    <StyledRichTextAdvancedText inline={inline} hasCustomFont={hasCustomFont}>
      {renderedNodes}
    </StyledRichTextAdvancedText>
  );
}
